import React, { useState, useEffect, useRef } from "react";
import {
    Button,
    Field,
    Label,
    Combobox,
    Option,
    CardHeader
} from "@fluentui/react-components";
import { ChevronDown20Regular, ChevronRight20Regular, Delete20Regular, Add20Filled } from "@fluentui/react-icons";
import GenerateAudioContainer from "../../Containers/GenericContainer/GenerateAudioContainer";
import FallBackContainer from "../../Containers/GenericContainer/FallBackContainer";
import "../CSS/holidaysComponent.css";
import { useTranslation } from "react-i18next";

const weekDays = [
    { key: 0, text: "Sunday" },
    { key: 1, text: "Monday" },
    { key: 2, text: "Tuesday" },
    { key: 3, text: "Wednesday" },
    { key: 4, text: "Thursday" },
    { key: 5, text: "Friday" },
    { key: 6, text: "Saturday" },
];

export default function HolidaysComponent({ callFlowData, setCallFlowData, ACTIVE_TENANT, permissions, isEditMode, defaultSection = 'holidays' }) {
    const { t } = useTranslation();

    const openHoursRef = useRef(null);

    const [holidays, setHolidays] = useState(callFlowData?.deskOpen?.holidays?.daysOff || []);
    const [weekendDays, setWeekendDays] = useState(callFlowData?.deskOpen?.holidays?.weekendDays || []);
    const [isHolidaysOpen, setIsHolidaysOpen] = useState(defaultSection === 'holidays');
    const [isOpenHoursOpen, setIsOpenHoursOpen] = useState(defaultSection === 'openHours');

    // Mettre à jour les sections ouvertes quand defaultSection change
    useEffect(() => {
        if (defaultSection === 'holidays') {
            setIsHolidaysOpen(true);
            setIsOpenHoursOpen(false);
        } else if (defaultSection === 'openHours') {
            setIsHolidaysOpen(false);
            setIsOpenHoursOpen(true);
            // Scroll vers la section Open Hours
            setTimeout(() => {
                openHoursRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [defaultSection]);

    // Mettre à jour holidays et weekendDays quand callFlowData change
    useEffect(() => {
        const newDaysOff = callFlowData?.deskOpen?.holidays?.daysOff || [];
        const newWeekendDays = callFlowData?.deskOpen?.holidays?.weekendDays || [];


        setHolidays(newDaysOff);
        setWeekendDays(newWeekendDays);
    }, [callFlowData?.deskOpen?.holidays?.daysOff, callFlowData?.deskOpen?.holidays?.weekendDays]);

    const [openHours, setOpenHours] = useState(
        weekDays.map((day) => ({
            key: day.key,
            text: day.text,
            isOpen: false,
            ranges: callFlowData?.deskOpen?.openHours?.[day.key] || [],
        }))
    );

    const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Fonction pour convertir dd/mm/yyyy vers yyyy-mm-dd
    const formatDateToISO = (dateStr) => {
        if (!dateStr) return "";
        if (dateStr.includes("-")) return dateStr; // Déjà au format ISO
        const [day, month, year] = dateStr.split("/");
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    };

    // Fonction pour convertir yyyy-mm-dd vers dd/mm/yyyy
    const formatDateToDisplay = (dateStr) => {
        if (!dateStr) return "";
        if (dateStr.includes("/")) return dateStr; // Déjà au format d'affichage
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
    };

    const updateCallFlow = (path, value) => {
        setCallFlowData((prev) => {
            const newData = { ...prev };
            const keys = path.split(".");
            let obj = newData;
            for (let i = 0; i < keys.length - 1; i++) {
                obj[keys[i]] = { ...obj[keys[i]] };
                obj = obj[keys[i]];
            }
            obj[keys[keys.length - 1]] = value;
            return newData;
        });
    };

    const weekendNames = weekendDays.map((d) => weekDays.find((w) => w.key === d)?.text || "").join(", ");

    const renderGenerateAudio = (audioPath, pathPrefix, disabled = false) => {
        const audioData = audioPath || {};

        // Normaliser le type
        let normalizedType = audioData.type || "Text2speech";
        if (normalizedType.toLowerCase() === "text2speech") {
            normalizedType = "Text2speech";
        } else if (normalizedType.toLowerCase() === "audiofile") {
            normalizedType = "Audiofile";
        }
        
        // Définir la langue par défaut si elle n'existe pas
        const defaultLanguage = audioData.language || callFlowData?.language || "en-US";
        
        // Déterminer la permission canRead selon le pathPrefix
        const canPlayAudio = pathPrefix.includes('holidays') 
            ? permissions?.holidays?.holidayActions?.canRead 
            : permissions?.holidays?.openHour?.canRead;
        
        return (
            <GenerateAudioContainer
                audioFileUrl={audioData.audioFileUrl}
                text={audioData.text || ""}
                type={normalizedType}
                defaultLanguage={defaultLanguage}
                disabled={disabled}
                setText={(text) => updateCallFlow(`${pathPrefix}.text`, text)}
                saveAudio={(url) => updateCallFlow(`${pathPrefix}.audioFileUrl`, url)}
                setType={(type) => updateCallFlow(`${pathPrefix}.type`, type)}
                setLangue={(lang) => updateCallFlow(`${pathPrefix}.language`, lang)}
                setAudio={(selectedMedia) => {
                    updateCallFlow(`${pathPrefix}`, {
                        ...audioData,
                        selectedMedia,
                        audioFileUrl: selectedMedia?.url || audioData.audioFileUrl || "",
                        type: "Audiofile",
                        text: audioData.text || "",
                    });
                }}
                fileName={audioData?.selectedMedia?.header}
                path={`${ACTIVE_TENANT}/${callFlowData.id}/${pathPrefix.split('.')[1]}`}
            />
        );
    };

    return (
        <div className="holidays-tab">
            {/* ================= HOLIDAYS SECTION ================= */}
            {permissions?.holidays?.holidayActions?.canRead && (
            <div className="section holidays-section">
                <div className="section-header holidays-header" onClick={() => setIsHolidaysOpen(!isHolidaysOpen)}>
                    {isHolidaysOpen ? <ChevronDown20Regular className="holidays-chevron" /> : <ChevronRight20Regular className="holidays-chevron" />}
                    {t("holidays")}
                </div>

                {isHolidaysOpen && (
                    <div className="section-content holidays-content">
                        {/* Holiday Message */}
                        <div className="subsection holiday-message-section">
                            <CardHeader header={<Label >{t("holidayMessage")}</Label>} />
                            <div className="holiday-message-content" >
                                {renderGenerateAudio(
                                    callFlowData?.deskOpen?.holidays?.audio,
                                    "deskOpen.holidays.audio",
                                    isEditMode ? !permissions?.holidays?.holidayActions?.canUpdate : !permissions?.holidays?.holidayActions?.canCreate
                                )}
                            </div>
                        </div>

                        {/* Select Holidays */}
                        <div className="subsection select-holidays-section">
                            <CardHeader header={<Label>{t("selectHolidays")}</Label>} />
                            <div className="subsection-content select-holidays-content">
                                <Button
                                    appearance="primary"
                                    className="add-holiday-button"
                                    disabled={isEditMode ? !permissions?.holidays?.holidayActions?.canUpdate : !permissions?.holidays?.holidayActions?.canCreate}
                                    onClick={() => {
                                        // Obtenir la date d'aujourd'hui au format dd/mm/yyyy
                                        const today = new Date();
                                        const day = String(today.getDate()).padStart(2, '0');
                                        const month = String(today.getMonth() + 1).padStart(2, '0');
                                        const year = today.getFullYear();
                                        const todayFormatted = `${day}/${month}/${year}`;
                                        
                                        const newHoliday = { id: generateUniqueId(), start: todayFormatted, end: todayFormatted };
                                        const updated = [...holidays, newHoliday];
                                        setHolidays(updated);
                                        updateCallFlow("deskOpen.holidays.daysOff", updated);
                                    }}
                                    icon={<Add20Filled />}
                                >
                                </Button>

                                {/* Header labels - displayed once */}
                                {holidays.length > 0 && (
                                    <div className="holidays-header-row">
                                        <Label>{t("startDate")}</Label>
                                        <Label>{t("endDate")}</Label>
                                        <div style={{ width: '32px' }}></div>
                                    </div>
                                )}

                                {holidays.map((holiday, idx) => (
                                    <div key={holiday.id} className="holiday-item holiday-row">
                                        <Field>
                                            <input
                                                type="date"
                                                value={formatDateToISO(holiday.start)}
                                                disabled={isEditMode ? !permissions?.holidays?.holidayActions?.canUpdate : !permissions?.holidays?.holidayActions?.canCreate}
                                                onChange={(e) => {
                                                    const updated = holidays.map((h, i) =>
                                                        i === idx ? { ...h, start: formatDateToDisplay(e.target.value) } : h
                                                    );
                                                    setHolidays(updated);
                                                    updateCallFlow("deskOpen.holidays.daysOff", updated);
                                                }}
                                            />
                                        </Field>
                                        <Field>
                                            <input
                                                type="date"
                                                value={formatDateToISO(holiday.end)}
                                                disabled={isEditMode ? !permissions?.holidays?.holidayActions?.canUpdate : !permissions?.holidays?.holidayActions?.canCreate}
                                                onChange={(e) => {
                                                    const updated = holidays.map((h, i) =>
                                                        i === idx ? { ...h, end: formatDateToDisplay(e.target.value) } : h
                                                    );
                                                    setHolidays(updated);
                                                    updateCallFlow("deskOpen.holidays.daysOff", updated);
                                                }}
                                            />
                                        </Field>
                                        <Button
                                            appearance="secondary"
                                            className="delete-holiday-button"
                                            disabled={isEditMode ? !permissions?.holidays?.holidayActions?.canUpdate : !permissions?.holidays?.holidayActions?.canCreate}
                                            onClick={() => {
                                                const updated = holidays.filter((_, i) => i !== idx);
                                                setHolidays(updated);
                                                updateCallFlow("deskOpen.holidays.daysOff", updated);
                                            }}
                                        >
                                            <Delete20Regular />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Weekend */}
                        <div className="subsection weekend-section">
                            <CardHeader header={<Label>{t("weekend")}</Label>} />
                            <div className="subsection-content weekend-content">
                                <Combobox
                                    multiselect
                                    placeholder={t("selectWeekendDays")}
                                    selectedOptions={weekendDays.map(
                                        (day) => weekDays.find((d) => d.key === day)?.text || ""
                                    )}
                                    value={weekendNames}
                                    disabled={isEditMode ? !permissions?.holidays?.holidayActions?.canUpdate : !permissions?.holidays?.holidayActions?.canCreate}
                                    onOptionSelect={(e, data) => {
                                        const selected = data.selectedOptions
                                            .map((opt) => weekDays.find((d) => d.text === opt)?.key)
                                            .filter((k) => k !== null);
                                        setWeekendDays(selected);
                                        updateCallFlow("deskOpen.holidays.weekendDays", selected);
                                    }}
                                >
                                    {weekDays.map((d) => (
                                        <Option key={d.key} value={d.text}>
                                            {t(d.text.toLowerCase())}
                                        </Option>
                                    ))}
                                </Combobox>
                            </div>
                        </div>

                        {/* Holidays fallback */}
                        <div className="subsection holidays-fallback-section">
                            <CardHeader header={<Label>{t("holidaysFallback")}</Label>} />
                            <div className="holidays-fallback-content">
                                <FallBackContainer
                                    typeValue={callFlowData?.deskOpen?.holidays?.holidaysAction?.type || "disconnect"}
                                    valueTargetType={callFlowData?.deskOpen?.holidays?.holidaysAction?.targetType || ""}
                                    targetId={callFlowData?.deskOpen?.holidays?.holidaysAction?.targetId || ""}
                                    valueName={callFlowData?.deskOpen?.holidays?.holidaysAction?.queueDisplayName || ""}
                                    displayName={callFlowData?.deskOpen?.holidays?.holidaysAction?.queueDisplayName || ""}
                                    isUpdate={true}
                                    disabled={isEditMode ? !permissions?.holidays?.holidayActions?.canUpdate : !permissions?.holidays?.holidayActions?.canCreate}
                                    setFallBackType={(value) =>
                                        updateCallFlow("deskOpen.holidays.holidaysAction.type", value)
                                    }
                                    updateTargetType={(_, data) =>
                                        updateCallFlow("deskOpen.holidays.holidaysAction.targetType", data.optionValue)
                                    }
                                    updateTargetId={(event, data) =>
                                        updateCallFlow("deskOpen.holidays.holidaysAction.targetId", data.value)
                                    }
                                    searchUser={(user, targetType) =>
                                        updateCallFlow("deskOpen.holidays.holidaysAction", {
                                            ...callFlowData.deskOpen.holidays.holidaysAction,
                                            queueDisplayName: user.displayName,
                                            targetId: user.id,
                                            // Déduit par l'éditeur : « app » si la cible est un
                                            // compte ressource Teams. Le figer à « user » ici
                                            // renvoyait un identifiant utilisateur inexistant.
                                            targetType: targetType || "user",
                                        })
                                    }
                                    onQueueSelect={(queue) =>
                                        updateCallFlow("deskOpen.holidays.holidaysAction", {
                                            ...callFlowData.deskOpen.holidays.holidaysAction,
                                            queueDisplayName: queue.queueName,
                                            queueId: queue.value,
                                            targetType: "queue",
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* ================= OPEN HOURS SECTION ================= */}
            {permissions?.holidays?.openHour?.canRead && (
            <div className="section open-hours-section" ref={openHoursRef}>
                <div className="section-header open-hours-header" onClick={() => setIsOpenHoursOpen(!isOpenHoursOpen)}>
                    {isOpenHoursOpen ? <ChevronDown20Regular className="holidays-chevron" /> : <ChevronRight20Regular className="holidays-chevron" />}
                    {t("openHours")}
                </div>

                {isOpenHoursOpen && (
                    <div className="section-content open-hours-content">
                        {/* Out of business hours message */}
                        <div className="subsection open-hours-message-section">
                            <CardHeader header={<Label >{t("outOfBusinessHoursMessage")}</Label>} />
                            <div className="open-hours-message-content" style={{marginLeft:'5rem'}}>
                                {renderGenerateAudio(
                                    callFlowData?.deskOpen?.openHours?.audio,
                                    "deskOpen.openHours.audio",
                                    isEditMode ? !permissions?.holidays?.openHour?.canUpdate : !permissions?.holidays?.openHour?.canCreate
                                )}
                            </div>
                        </div>

                        {/* Week schedule */}
                        {openHours.map((day, dayIdx) => (
                            <div key={day.key} className="subsection open-hours-day-section">
                                <div
                                    className="day-header open-hours-day-header"
                                    onClick={() => {
                                        const updated = [...openHours];
                                        updated[dayIdx].isOpen = !updated[dayIdx].isOpen;
                                        setOpenHours(updated);
                                    }}
                                >
                                    {day.isOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                                    <Label>{t(day.text.toLowerCase())}</Label>
                                </div>

                                {day.isOpen && (
                                    <div className="subsection-content open-hours-day-content">
                                        {/* Header labels - displayed once per day */}
                                        {day.ranges.length > 0 && (
                                            <div className="holidays-header-row open-hours-header-row">
                                                <Label>{t("startTime")}</Label>
                                                <Label>{t("endTime")}</Label>
                                                <div style={{ width: '32px' }}></div>
                                            </div>
                                        )}

                                        {day.ranges.map((range, rangeIdx) => (
                                            <div key={rangeIdx} className="holiday-item open-hours-range-row">
                                                <Field>
                                                    <input
                                                        type="time"
                                                        value={range.start}
                                                        disabled={isEditMode ? !permissions?.holidays?.openHour?.canUpdate : !permissions?.holidays?.openHour?.canCreate}
                                                        onChange={(e) => {
                                                            const updated = [...openHours];
                                                            updated[dayIdx].ranges[rangeIdx].start = e.target.value;
                                                            setOpenHours(updated);
                                                            updateCallFlow("deskOpen.openHours", {
                                                                ...callFlowData.deskOpen.openHours,
                                                                [day.key]: updated[dayIdx].ranges,
                                                            });
                                                        }}
                                                    />
                                                </Field>
                                                <Field>
                                                    <input
                                                        type="time"
                                                        value={range.end}
                                                        disabled={isEditMode ? !permissions?.holidays?.openHour?.canUpdate : !permissions?.holidays?.openHour?.canCreate}
                                                        onChange={(e) => {
                                                            const updated = [...openHours];
                                                            updated[dayIdx].ranges[rangeIdx].end = e.target.value;
                                                            setOpenHours(updated);
                                                            updateCallFlow("deskOpen.openHours", {
                                                                ...callFlowData.deskOpen.openHours,
                                                                [day.key]: updated[dayIdx].ranges,
                                                            });
                                                        }}
                                                    />
                                                </Field>
                                                <Button
                                                    appearance="secondary"
                                                    className="delete-range-button"
                                                    disabled={isEditMode ? !permissions?.holidays?.openHour?.canUpdate : !permissions?.holidays?.openHour?.canCreate}
                                                    onClick={() => {
                                                        const updated = [...openHours];
                                                        updated[dayIdx].ranges.splice(rangeIdx, 1);
                                                        setOpenHours(updated);
                                                        updateCallFlow("deskOpen.openHours", {
                                                            ...callFlowData.deskOpen.openHours,
                                                            [day.key]: updated[dayIdx].ranges,
                                                        });
                                                    }}
                                                >
                                                    <Delete20Regular />
                                                </Button>
                                            </div>
                                        ))}
                                        <Button
                                            appearance="primary"
                                            className="add-range-button"
                                            disabled={isEditMode ? !permissions?.holidays?.openHour?.canUpdate : !permissions?.holidays?.openHour?.canCreate}
                                            onClick={() => {
                                                const newRange = { id: generateUniqueId(), start: "08:00", end: "17:00" };
                                                const updated = [...openHours];
                                                updated[dayIdx].ranges.push(newRange);
                                                setOpenHours(updated);
                                                updateCallFlow("deskOpen.openHours", {
                                                    ...callFlowData.deskOpen.openHours,
                                                    [day.key]: updated[dayIdx].ranges,
                                                });
                                            }}
                                        >
                                            {t("addTimeRange")}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Open Hours fallback */}
                        <div className="subsection open-hours-fallback-section">
                            <CardHeader header={<Label>{t("openHoursFallback")}</Label>} />
                            <div className="open-hours-fallback-content">
                                <FallBackContainer
                                    typeValue={callFlowData?.deskOpen?.openHours?.openHoursAction?.type || "disconnect"}
                                    valueTargetType={callFlowData?.deskOpen?.openHours?.openHoursAction?.targetType || ""}
                                    targetId={callFlowData?.deskOpen?.openHours?.openHoursAction?.targetId || ""}
                                    valueName={callFlowData?.deskOpen?.openHours?.openHoursAction?.queueDisplayName || ""}
                                    displayName={callFlowData?.deskOpen?.openHours?.openHoursAction?.queueDisplayName || ""}
                                    isUpdate={true}
                                    disabled={isEditMode ? !permissions?.holidays?.openHour?.canUpdate : !permissions?.holidays?.openHour?.canCreate}
                                    setFallBackType={(value) =>
                                        updateCallFlow("deskOpen.openHours.openHoursAction.type", value)
                                    }
                                    updateTargetType={(_, data) =>
                                        updateCallFlow("deskOpen.openHours.openHoursAction.targetType", data.optionValue)
                                    }
                                    updateTargetId={(event, data) =>
                                        updateCallFlow("deskOpen.openHours.openHoursAction.targetId", data.value)
                                    }
                                    searchUser={(user, targetType) =>
                                        updateCallFlow("deskOpen.openHours.openHoursAction", {
                                            ...callFlowData.deskOpen.openHours.openHoursAction,
                                            queueDisplayName: user.displayName,
                                            targetId: user.id,
                                            // Déduit par l'éditeur : « app » si la cible est un
                                            // compte ressource Teams. Le figer à « user » ici
                                            // renvoyait un identifiant utilisateur inexistant.
                                            targetType: targetType || "user",
                                        })
                                    }
                                    onQueueSelect={(queue) =>
                                        updateCallFlow("deskOpen.openHours.openHoursAction", {
                                            ...callFlowData.deskOpen.openHours.openHoursAction,
                                            queueDisplayName: queue.queueName,
                                            queueId: queue.value,
                                            targetType: "queue",
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            )}
        </div>
    );
}
